/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	type Context,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from 'react';

import { jsx } from '@compiled/react';
import { IntlProvider } from 'react-intl';

import { resolveInitialLocale } from './detect-locale';
import { DEFAULT_LOCALE, getDescriptor, type LocaleCode, type LocaleMessages } from './locales';
import { writePersistedLocale } from './storage';

type LocaleContextValue = {
	locale: LocaleCode;
	setLocale: (next: LocaleCode) => void;
};

export const LocaleContext: Context<LocaleContextValue | null> =
	createContext<LocaleContextValue | null>(null);

export type LocaleProviderProps = {
	children: ReactNode;
	/**
	 * Optional override, primarily for Storybook / VR examples that want to
	 * pin a specific locale. Production code should leave this unset and
	 * let the persisted/browser/fallback resolver decide.
	 */
	initialLocale?: LocaleCode;
};

/**
 * Owns the active locale for the microsite, lazily loads the matching
 * Traduki messages bundle, and renders <IntlProvider> for descendants.
 *
 * On first render: messages are not yet loaded, so we render with
 * `messages={undefined}` — react-intl then falls back to each
 * <FormattedMessage>'s `defaultMessage`, which is the English source
 * string. This avoids a flash of empty strings while the bundle loads.
 */
export const LocaleProvider = ({ children, initialLocale }: LocaleProviderProps): JSX.Element => {
	const [locale, setLocaleState] = useState<LocaleCode>(
		() => initialLocale ?? resolveInitialLocale(),
	);
	const [messages, setMessages] = useState<LocaleMessages | undefined>(undefined);

	useEffect(() => {
		let cancelled = false;
		const descriptor = getDescriptor(locale);
		descriptor
			.loadMessages()
			.then((loaded) => {
				if (!cancelled) {
					setMessages(loaded);
				}
			})
			.catch((err) => {
				// Non-fatal: log to console; surfaces to Sentry via the
				// global error handler already wired by initSentry.
				// eslint-disable-next-line no-console
				console.error(`[LocaleProvider] Failed to load messages for "${locale}"`, err);
			});
		return () => {
			cancelled = true;
		};
	}, [locale]);

	const setLocale = useCallback((next: LocaleCode) => {
		writePersistedLocale(next);
		setLocaleState(next);
	}, []);

	const ctx = useMemo<LocaleContextValue>(() => ({ locale, setLocale }), [locale, setLocale]);

	return (
		<LocaleContext.Provider value={ctx}>
			<IntlProvider locale={locale} defaultLocale={DEFAULT_LOCALE} messages={messages}>
				{children}
			</IntlProvider>
		</LocaleContext.Provider>
	);
};

export const useLocale = (): LocaleCode => {
	const ctx = useContext(LocaleContext);
	if (!ctx) {
		throw new Error('useLocale must be used inside <LocaleProvider>');
	}
	return ctx.locale;
};

export const useSetLocale = (): ((next: LocaleCode) => void) => {
	const ctx = useContext(LocaleContext);
	if (!ctx) {
		throw new Error('useSetLocale must be used inside <LocaleProvider>');
	}
	return ctx.setLocale;
};

// Re-export catalog so consumers can build their own selectors if needed.
export {
	DEFAULT_LOCALE,
	SELECTABLE_LOCALES,
	SUPPORTED_LOCALES,
	type LocaleCode,
	type LocaleDescriptor,
} from './locales';
